
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ServiceCategorySelectorProps {
  practitionerId: string;
  onUpdate: () => void;
}

interface ServiceCategory {
  id: number;
  name: string;
  display_name_en: string;
  display_name_zh: string;
  description: string;
  sort_order?: number;
}

export default function ServiceCategorySelector({ practitionerId, onUpdate }: ServiceCategorySelectorProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  useEffect(() => {
    fetchData();
  }, [practitionerId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Fetch all service categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('service_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('display_name_en', { ascending: true });

      if (categoriesError) throw categoriesError;

      // Fetch practitioner's selected categories
      const { data: practitionerCategories, error: practitionerError } = await supabase
        .from('practitioner_service_categories')
        .select('category_id')
        .eq('practitioner_id', practitionerId);

      if (practitionerError) throw practitionerError;

      setCategories(categoriesData || []);
      setSelectedCategories(practitionerCategories?.map(pc => pc.category_id) || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error",
        description: "Failed to load service categories",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    if (checked) {
      setSelectedCategories(prev => [...prev, categoryId]);
    } else {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // First, delete all existing category associations
      const { error: deleteError } = await supabase
        .from('practitioner_service_categories')
        .delete()
        .eq('practitioner_id', practitionerId);

      if (deleteError) throw deleteError;

      // Then, insert the new selected categories
      if (selectedCategories.length > 0) {
        const insertData = selectedCategories.map(categoryId => ({
          practitioner_id: practitionerId,
          category_id: categoryId
        }));

        const { error: insertError } = await supabase
          .from('practitioner_service_categories')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      toast({
        title: "Success",
        description: "Service categories updated successfully",
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating categories:', error);
      toast({
        title: "Error",
        description: "Failed to update service categories",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDisplayName = (category: ServiceCategory) => {
    return i18n.language === 'zh' ? category.display_name_zh : category.display_name_en;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('serviceCategories.required')}</CardTitle>
        <p className="text-sm text-gray-600">
          {t('serviceCategories.description')}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {categories.map((category) => (
              <div key={category.id} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={selectedCategories.includes(category.id)}
                    onCheckedChange={(checked) => handleCategoryChange(category.id, checked as boolean)}
                    disabled={isSubmitting}
                  />
                  <Label 
                    htmlFor={`category-${category.id}`}
                    className="text-base font-medium cursor-pointer"
                  >
                    {getDisplayName(category)}
                  </Label>
                </div>
                {category.description && (
                  <p className="text-sm text-gray-600 ml-6">
                    {category.description}
                  </p>
                )}
              </div>
            ))}
          </div>

          <Button 
            type="submit" 
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('basicInformation.saving')}
              </>
            ) : (
              t('serviceCategories.saveCategories')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
